import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, MapPin, ChevronDown, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const catNav = [
  { to: "/shop", label: "All" },
  { to: "/shop/women", label: "Women" },
  { to: "/shop/men", label: "Men" },
  { to: "/shop/accessories", label: "Accessories" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    const url = q.trim() ? `/shop?q=${encodeURIComponent(q.trim())}` : "/shop";
    navigate(url);
  };

  return (
    <header className="sticky top-0 z-40" data-testid="site-header">
      <div className="bg-[#1a1d29] text-white">
        <div className="max-w-[1500px] mx-auto flex items-center gap-3 px-3 md:px-6 h-[68px]">
          <button
            className="md:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <Link
            to="/"
            className="shrink-0 px-2 py-1 border border-transparent hover:border-white/40 rounded-sm"
            data-testid="brand-logo"
          >
            <div className="font-display text-2xl md:text-3xl font-semibold text-white leading-none">
              shopnest<span className="text-[#c8532e]">.</span>
            </div>
          </Link>

          <button className="hidden lg:flex items-center px-2 py-1 border border-transparent hover:border-white/40 rounded-sm text-sm">
            <MapPin className="w-4 h-4 mr-1.5" />
            <div className="text-left leading-tight">
              <div className="text-[11px] text-neutral-300">Deliver to</div>
              <div className="text-sm font-semibold -mt-0.5">India</div>
            </div>
          </button>

          <form onSubmit={submitSearch} className="flex-1 min-w-0 mx-1 md:mx-2 flex h-11 rounded-full overflow-hidden bg-white text-black" data-testid="search-form">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              type="text"
              placeholder="Search clothing, accessories & more"
              className="flex-1 min-w-0 px-5 text-sm outline-none"
              data-testid="search-input"
            />
            <button
              type="submit"
              className="bg-[#c8532e] hover:bg-[#a8431f] px-5 flex items-center justify-center text-white transition-colors"
              data-testid="search-submit"
              aria-label="search"
            >
              <Search className="w-5 h-5" />
            </button>
          </form>

          {user ? (
            <div className="hidden md:block relative group px-2 py-1 border border-transparent hover:border-white/40 rounded-sm">
              <div className="text-[11px] leading-tight text-neutral-300">Hi, {user.name.split(" ")[0]}</div>
              <div className="text-sm font-semibold flex items-center leading-tight">
                Account <ChevronDown className="w-3 h-3 ml-0.5" />
              </div>
              <div className="absolute right-0 top-full pt-1 hidden group-hover:block w-56 z-50">
                <div className="bg-white text-black shadow-xl rounded border border-black/5">
                  {user.is_admin && (
                    <Link to="/admin" className="block px-4 py-2 text-sm hover:bg-neutral-100" data-testid="dropdown-admin">
                      Admin Console
                    </Link>
                  )}
                  <Link to="/orders" className="block px-4 py-2 text-sm hover:bg-neutral-100" data-testid="dropdown-orders">
                    Your Orders
                  </Link>
                  <Link to="/addresses" className="block px-4 py-2 text-sm hover:bg-neutral-100" data-testid="dropdown-addresses">
                    Address Book
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      navigate("/");
                    }}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-neutral-100"
                    data-testid="dropdown-signout"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <Link
              to="/login"
              className="hidden md:block px-2 py-1 border border-transparent hover:border-white/40 rounded-sm"
              data-testid="nav-signin"
            >
              <div className="text-[11px] leading-tight text-neutral-300">Hello,</div>
              <div className="text-sm font-semibold flex items-center leading-tight">
                Sign in <ChevronDown className="w-3 h-3 ml-0.5" />
              </div>
            </Link>
          )}

          <Link
            to="/cart"
            className="relative flex items-end px-2 py-1 border border-transparent hover:border-white/40 rounded-sm"
            data-testid="nav-cart-button"
          >
            <div className="relative">
              <ShoppingBag className="w-7 h-7" strokeWidth={1.5} />
              <span
                className="absolute -top-1 -right-2 bg-[#c8532e] text-white font-bold text-[11px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            </div>
            <span className="ml-2 font-semibold text-sm hidden md:inline">Bag</span>
          </Link>
        </div>

        <div className="bg-[#2a2e3d] text-white">
          <div className="max-w-[1500px] mx-auto flex items-center gap-1 px-3 md:px-6 h-11 overflow-x-auto text-sm">
            <div className="flex items-center gap-2 font-semibold px-3 py-1 border border-transparent hover:border-white/40 rounded-sm shrink-0">
              <Menu className="w-4 h-4" /> All
            </div>
            {catNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end
                className={({ isActive }) =>
                  `px-3 py-1 border border-transparent hover:border-white/40 rounded-sm shrink-0 ${isActive ? "font-semibold" : ""}`
                }
                data-testid={`nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </NavLink>
            ))}
            <div className="hidden md:block ml-4 text-neutral-300 shrink-0">Today's Deals</div>
            <div className="hidden md:block px-3 text-neutral-300 shrink-0">New Arrivals</div>
            <div className="hidden md:block px-3 text-[#e0a500] font-semibold shrink-0">Use WELCOME10 for 10% off</div>
            {user?.is_admin && (
              <Link
                to="/admin"
                className="ml-auto px-3 py-1 border border-transparent hover:border-white/40 rounded-sm shrink-0 text-[#c8532e] font-semibold"
                data-testid="nav-admin"
              >
                Admin Console
              </Link>
            )}
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-b border-[#e0d8c8]">
          <div className="px-4 py-4 flex flex-col gap-3">
            {catNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                className="text-sm"
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </NavLink>
            ))}
            <div className="h-px bg-[#e0d8c8]" />
            {user ? (
              <>
                {user.is_admin && (
                  <Link to="/admin" onClick={() => setMobileOpen(false)} className="text-sm">
                    Admin Console
                  </Link>
                )}
                <Link to="/orders" onClick={() => setMobileOpen(false)} className="text-sm">Your Orders</Link>
                <Link to="/addresses" onClick={() => setMobileOpen(false)} className="text-sm">Address Book</Link>
                <button
                  onClick={() => {
                    logout();
                    setMobileOpen(false);
                    navigate("/");
                  }}
                  className="text-left text-sm"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="text-sm">Sign In</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
